import { createSelector } from 'reselect';
import { CallbackSaga, createGeneralCall } from '@altermaind/core/requests-status-management';
import { HTTP_CONFLICT } from '@/_shared/constants/httpStatusCode';
import { IS_MOCK_ENABLED } from '@/_shared/constants/mock';
import { GenericPost } from '@/_shared/types';
import { GenericApiError } from '@/_shared/types/apiError';
import { getCurrentOrganization } from '@/core/state/selectors';
import { API_ORGANIZATIONS_ORGANIZATIONID_DEPARTMENTS_PATH_KEY } from '@/swagger/constants/anagraphics-keys';
import { KEY_POST_CREATE_DEPARTMENT } from '../constants/keys';
import { API_POST_CREATE_DEPARTMENT } from '../constants/urls';
import { MOCK_RESPONSE_POST_CREATE_DEPARTMENT } from '../mocks';

type FullRequestType = ReturnType<typeof createGeneralCall>['selectors']['request'] & {
  payload: Omit<CallParamsPostCreateDepartment, 'successSaga' | 'errorSaga'>;
};

type RequestPostCreateDepartment = GenericPost<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_DEPARTMENTS_PATH_KEY
>['request']['body'];

export type CallParamsPostCreateDepartment = RequestPostCreateDepartment & {
  successSaga?: CallbackSaga;
  errorSaga?: CallbackSaga;
};

export type CallResponsePostCreateDepartment = GenericPost<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_DEPARTMENTS_PATH_KEY
>['response']['body'];

type CallErrorPostCreateDepartment = GenericApiError;

type CallSelectorsPostCreateDepartment = {
  isDuplicateName: boolean;
  fullRequest: FullRequestType;
};

let internalPayload = {};

export const postCreateDepartmentCall = createGeneralCall<
  CallParamsPostCreateDepartment,
  CallResponsePostCreateDepartment,
  CallErrorPostCreateDepartment,
  CallSelectorsPostCreateDepartment
>({
  id: KEY_POST_CREATE_DEPARTMENT,
  url: () => state => {
    const org = getCurrentOrganization(state);
    return API_POST_CREATE_DEPARTMENT(org as unknown as string);
  },
  method: 'post',
  paramsSelector:
    ({ payload }) =>
    () => {
      internalPayload = { ...payload };
      return payload;
    },
  successMock: IS_MOCK_ENABLED() ? MOCK_RESPONSE_POST_CREATE_DEPARTMENT : undefined,
  responseExtractor: null,
  selectors: {
    isDuplicateName: ({ selectors }) =>
      createSelector(selectors.error, error => {
        return error?.status === HTTP_CONFLICT;
      }),
    fullRequest: ({ selectors }) =>
      createSelector(selectors.request, request => {
        return {
          ...request,
          payload: { ...internalPayload },
        } as unknown as FullRequestType;
      }),
  },
});
